import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";
import { HelmShell } from "@/components/helm/HelmShell";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SeniorBenefitAdvisors from "@/pages/sba/SeniorBenefitAdvisors";
import SBAAssessment from "@/pages/sba/AssessmentWizard";
import SBAAssessmentResults from "@/pages/sba/AssessmentResults";
import FounderFrontDoor from "@/pages/sba/FounderFrontDoor";
import { LIFE_CONFIG } from "@/pages/assessment/config";
import LifeEntry from "@/pages/life/LifeEntry";
import Dashboard from "@/pages/employee/Dashboard";
import Household from "@/pages/employee/Household";
import Benefits from "@/pages/employee/Benefits";
import BenefitCategory from "@/pages/employee/BenefitCategory";
import ProductDetail from "@/pages/employee/ProductDetail";
import Enroll from "@/pages/employee/Enroll";
import Enrollments from "@/pages/employee/Enrollments";
import Profile from "@/pages/employee/Profile";
import ConsumerMedicare from "@/pages/employee/ConsumerMedicare";
import EmployerConsole from "@/pages/employer/EmployerConsole";
import AdvisorPortal from "@/pages/advisor/AdvisorPortal";
import MedicareJourney from "@/pages/advisor/MedicareJourney";
import HandoffHistory from "@/pages/advisor/HandoffHistory";
import AdminConsole from "@/pages/admin/AdminConsole";
import EnrollmentRouter from "@/pages/admin/EnrollmentRouter";
import AgentWorkspace from "@/pages/agent/AgentWorkspace";
import HelmHouseholds from "@/pages/agent/HelmHouseholds";
import HelmDocuments from "@/pages/agent/HelmDocuments";
import HelmAsk from "@/pages/agent/HelmAsk";
import HelmSettings from "@/pages/agent/HelmSettings";
import HelmWorkLog from "@/pages/agent/HelmWorkLog";
import HelmRoster from "@/pages/agent/HelmRoster";
import HelmAgencyOverview from "@/pages/agent/HelmAgencyOverview";
import HelmFunnel from "@/pages/agent/HelmFunnel";
import HelmMyBook from "@/pages/agent/HelmMyBook";
import HelmMessages from "@/pages/agent/HelmMessages";
import HouseholdProfile from "@/pages/agent/HouseholdProfile";
import AskNauticus from "@/pages/AskNauticus";
import AskQA from "@/pages/admin/AskQA";
import PlanIntel from "@/pages/admin/PlanIntel";
import PartDIntel from "@/pages/admin/PartDIntel";
import ProviderIntel from "@/pages/admin/ProviderIntel";
import AskOrchestratorQA from "@/pages/admin/AskOrchestratorQA";
import EmailPreview from "@/pages/admin/EmailPreview";
import SharedComparison from "@/pages/compare/SharedComparison";
import MemberHome from "@/pages/member/MemberHome";
import PrivacySecurity from "@/pages/PrivacySecurity";
import MemberAskNauticus from "@/pages/member/AskNauticus";
import ReviewJourney from "@/pages/member/ReviewJourney";

function Shell({ roles, children }) {
  return (
    <ProtectedRoute roles={roles}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function Helm({ children, roles = ["advisor", "platform_admin"] }) {
  return (
    <ProtectedRoute roles={roles}>
      <HelmShell>{children}</HelmShell>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/ask" element={<AskNauticus />} />
            <Route path="/privacy" element={<PrivacySecurity />} />
            <Route path="/senior-benefit-advisors" element={<SeniorBenefitAdvisors />} />
            <Route path="/sba" element={<SeniorBenefitAdvisors />} />
            <Route path="/sba/assessment" element={<SBAAssessment />} />
            <Route path="/sba/assessment/results" element={<SBAAssessmentResults />} />
            <Route path="/f/:slug" element={<FounderFrontDoor />} />
            <Route path="/life" element={<LifeEntry />} />
            <Route path="/life/assessment" element={<SBAAssessment config={LIFE_CONFIG} />} />
            <Route path="/life/assessment/results" element={<SBAAssessmentResults config={LIFE_CONFIG} />} />
            <Route path="/login" element={<Login />} />
            <Route path="/compare/:token" element={<SharedComparison />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Employee portal */}
            <Route path="/app" element={<Shell roles={["employee"]}><Dashboard /></Shell>} />
            <Route path="/app/household" element={<Shell roles={["employee"]}><Household /></Shell>} />
            <Route path="/app/benefits" element={<Shell roles={["employee"]}><Benefits /></Shell>} />
            <Route path="/app/benefits/:key" element={<Shell roles={["employee"]}><BenefitCategory /></Shell>} />
            <Route path="/app/product/:id" element={<Shell roles={["employee"]}><ProductDetail /></Shell>} />
            <Route path="/app/enroll/:id" element={<Shell roles={["employee"]}><Enroll /></Shell>} />
            <Route path="/app/enrollments" element={<Shell roles={["employee"]}><Enrollments /></Shell>} />
            <Route path="/app/medicare" element={<Shell roles={["employee"]}><ConsumerMedicare /></Shell>} />
            <Route path="/app/member" element={<Shell roles={["employee"]}><MemberHome /></Shell>} />
            <Route path="/app/review" element={<Shell roles={["employee"]}><ReviewJourney /></Shell>} />
            <Route path="/app/ask" element={<Shell roles={["employee"]}><MemberAskNauticus /></Shell>} />
            <Route path="/app/profile" element={<Shell roles={["employee"]}><Profile /></Shell>} />

            {/* Employer portal */}
            <Route path="/employer" element={<Shell roles={["employer_admin", "platform_admin"]}><EmployerConsole /></Shell>} />

            {/* Advisor portal */}
            <Route path="/advisor" element={<Shell roles={["advisor", "platform_admin"]}><AdvisorPortal /></Shell>} />
            <Route path="/advisor/medicare" element={<Shell roles={["advisor", "platform_admin"]}><MedicareJourney /></Shell>} />
            <Route path="/advisor/handoffs" element={<Shell roles={["advisor", "platform_admin"]}><HandoffHistory /></Shell>} />
            <Route path="/agent" element={<Helm><AgentWorkspace /></Helm>} />
            <Route path="/agent/households" element={<Helm><HelmHouseholds /></Helm>} />
            <Route path="/agent/my-book" element={<Helm><HelmMyBook /></Helm>} />
            <Route path="/agent/households/:id" element={<Helm><HouseholdProfile /></Helm>} />
            <Route path="/agent/messages" element={<Helm><HelmMessages /></Helm>} />
            <Route path="/agent/documents" element={<Helm><HelmDocuments /></Helm>} />
            <Route path="/agent/worklog" element={<Helm><HelmWorkLog /></Helm>} />
            <Route path="/agent/roster" element={<Helm><HelmRoster /></Helm>} />
            <Route path="/agent/agency" element={<Helm roles={["platform_admin"]}><HelmAgencyOverview /></Helm>} />
            <Route path="/agent/funnel" element={<Helm><HelmFunnel /></Helm>} />
            <Route path="/agent/ask" element={<Helm><HelmAsk /></Helm>} />
            <Route path="/agent/settings" element={<Helm><HelmSettings /></Helm>} />

            {/* Platform admin */}
            <Route path="/admin" element={<Shell roles={["platform_admin"]}><AdminConsole /></Shell>} />
            <Route path="/admin/enrollment" element={<Shell roles={["platform_admin"]}><EnrollmentRouter /></Shell>} />
            <Route path="/admin/ask-qa" element={<Shell roles={["platform_admin"]}><AskQA /></Shell>} />
            <Route path="/admin/plan-intel" element={<Shell roles={["platform_admin"]}><PlanIntel /></Shell>} />
            <Route path="/admin/part-d-intel" element={<Shell roles={["platform_admin"]}><PartDIntel /></Shell>} />
            <Route path="/admin/provider-intel" element={<Shell roles={["platform_admin"]}><ProviderIntel /></Shell>} />
            <Route path="/admin/ask-orchestrator" element={<Shell roles={["platform_admin"]}><AskOrchestratorQA /></Shell>} />
            <Route path="/admin/email-preview" element={<Shell roles={["platform_admin"]}><EmailPreview /></Shell>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
